/**
 * Карточка сделки, быстрые задачи, звонок — вызовы GiperCRM для экрана переписки (этап 2b).
 * Каждый адрес сверен с кодом CRM (GiperCRM/server/src/routes/*), ссылки — в комментариях у функций.
 */
import { crmFetch } from './crm';
import { cwFetch } from './chatwoot';

// ── Карточка по переписке ──

export interface CardStage {
  id: string;
  name: string;
  color?: string | null;
  position: number;
  /** lead | working | won | lost … */
  stageType?: string;
}

export interface CardPerson {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  emails?: string[] | null;
  phones?: string[] | null;
  jobTitle?: string | null;
}

export interface CardDeal {
  id: string;
  title: string;
  /** Деньги CRM отдаёт строкой (Decimal). */
  amount?: string | number | null;
  currency?: string | null;
  stageId: string;
  pipelineId: string;
  ownerId?: string | null;
  priority?: string | null;
  leadTemperature?: string | null;
  source?: string | null;
  description?: string | null;
  expectedCloseDate?: string | null;
  /** Номер переписки Chatwoot, к которой привязана сделка (первая; остальные — «дополнительные»). */
  chatwootConversationId?: number | null;
  chatwootDisplayId?: number | null;
  stage?: CardStage | null;
  pipeline?: { id: string; name: string } | null;
  person?: CardPerson | null;
  company?: { id: string; name: string } | null;
}

export interface CardEventBrief {
  kind: string;
  source: string;
  title: string;
  occurredAt: string;
}

export interface CardResponse {
  found: boolean;
  /** other_project — переписка другого проекта. */
  reason?: string;
  message?: string;
  person?: CardPerson | null;
  deal?: CardDeal | null;
  stages?: CardStage[];
  events?: CardEventBrief[];
  cardPath?: string | null;
}

export interface CardQuery {
  /**
   * Кабинет Chatwoot и видимый номер переписки (display_id). Видимый номер уникален только
   * внутри кабинета, а CRM хранит внутренний — поэтому шлём ПАРУ, и CRM сама находит настоящую
   * переписку (resolveConversationByDisplayId). Видимый номер вместо внутреннего показал бы
   * чужую сделку с совпавшим внутренним номером.
   */
  accountId: number;
  displayId: number;
  contactId?: number;
  email?: string;
  phone?: string;
}

/** GET /api/crm/chatwoot/card (GiperCRM/server/src/routes/chatwoot.ts ~314). */
export function getCard(q: CardQuery): Promise<CardResponse> {
  const p = new URLSearchParams();
  p.set('chatwootAccountId', String(q.accountId));
  p.set('displayId', String(q.displayId));
  if (q.contactId) p.set('contactId', String(q.contactId));
  if (q.email) p.set('email', q.email);
  if (q.phone) p.set('phone', q.phone);
  return crmFetch<CardResponse>(`/api/crm/chatwoot/card?${p.toString()}`);
}

export interface LeadInput {
  name: string;
  phone?: string | null;
  email?: string | null;
  chatwootContactId?: number;
  /** Кабинет и видимый номер переписки — только для лида из переписки; «Новый лид» из «Воронки» без них. */
  chatwootAccountId?: number;
  /** Видимый номер переписки; настоящий CRM найдёт сама по паре «кабинет + видимый номер». */
  displayId?: number;
  /** Воронка, к которой привязан канал; пусто — основная воронка того, кто заводит. */
  pipelineId?: string | null;
  /** Источник (ключ справочника CRM); по умолчанию — Chatwoot, как у лида из переписки. */
  source?: string | null;
}

/** POST /api/crm/deals/lead (routes/deals.ts ~701). Клиент — вложенным person; воронка — основная того, кто заводит. */
export function createLead(i: LeadInput) {
  return crmFetch<{ id: string }>('/api/crm/deals/lead', {
    method: 'POST',
    body: JSON.stringify({
      source: i.source || 'Chatwoot',
      person: {
        firstName: i.name,
        phones: i.phone ? [i.phone] : [],
        emails: i.email ? [i.email] : [],
      },
      ...(i.pipelineId ? { pipelineId: i.pipelineId } : {}),
      chatwootContactId: i.chatwootContactId,
      chatwootAccountId: i.chatwootAccountId,
      chatwootDisplayId: i.displayId,
    }),
  });
}

/**
 * GET /api/crm/inbox-bindings → в какую воронку CRM «смотрит» канал Chatwoot (routes/inboxBindings.ts ~19).
 * Нужна, чтобы лид из переписки встал в воронку своего канала, а не в «основную».
 */
export async function getInboxPipelineId(accountId: number, inboxId: number): Promise<string | null> {
  const r = await crmFetch<{ data: Array<{ chatwootInboxId: number; accountId: number; binding: { pipelineId: string } | null }> }>(
    '/api/crm/inbox-bindings',
  );
  return r.data?.find((b) => b.chatwootInboxId === inboxId && b.accountId === accountId)?.binding?.pipelineId ?? null;
}

// ── Смена стадии ──

/** POST /api/crm/deals/:id/move — напрямую в CRM (routes/deals.ts ~480). */
export function moveDeal(dealId: string, stageId: string, reason?: string) {
  return crmFetch<unknown>(`/api/crm/deals/${dealId}/move`, {
    method: 'POST',
    body: JSON.stringify({ stageId, ...(reason ? { reason } : {}) }),
  });
}

export interface LostReason { id: string; name: string; needsComment: boolean }

/** GET /api/crm/sales/reasons → { items } — справочник причин отказа (routes/sales.ts ~255). */
export async function getLostReasons(): Promise<LostReason[]> {
  const r = await crmFetch<{ items: LostReason[] }>('/api/crm/sales/reasons');
  return r.items ?? [];
}

/** POST /api/crm/deals/:id/disqualify — отказ с причиной из справочника (routes/deals.ts ~945). */
export function disqualifyDeal(dealId: string, choice: { reasonId: string; comment?: string; stageId?: string }) {
  return crmFetch<unknown>(`/api/crm/deals/${dealId}/disqualify`, { method: 'POST', body: JSON.stringify(choice) });
}

/**
 * POST /api/v1/accounts/:acc/conversations/:id/crm_stage — наш Chatwoot: сперва спрашивает CRM, пишет у себя
 * только после её согласия (conversations_controller#crm_stage → GiperCrm::StageClient, _giper/STAGES_ARCHITECTURE.md).
 * Отказ CRM приходит текстом в error.
 */
export function setConversationStage(accountId: number, displayId: number, crmStageId: string) {
  return cwFetch<{ deal_id?: string; stage_id?: string; stage_name?: string; deal_url?: string }>(
    `/api/v1/accounts/${accountId}/conversations/${displayId}/crm_stage`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ crm_stage_id: crmStageId }) },
  );
}

// ── Люди ──

export interface Colleague { id: string; name: string; me: boolean; position?: string | null }

/** GET /api/crm/colleagues → { data } — имена ответственных (routes/colleagues.ts). */
export async function getColleagues(): Promise<Colleague[]> {
  const r = await crmFetch<{ data: Colleague[] }>('/api/crm/colleagues');
  return r.data ?? [];
}

// ── Быстрые задачи ──

export interface QuickTaskPreset {
  key: string;
  title: string;
  dueInDays: number;
  /** Срок готовой датой ГГГГ-ММ-ДД, посчитанной сервером (местные сутки). */
  dueDate: string;
  color?: string | null;
}

/** GET /api/crm/quick-tasks?pipelineId= → { data } — заготовки (routes/quickTasks.ts). */
export async function getQuickTasks(pipelineId?: string | null): Promise<QuickTaskPreset[]> {
  const q = pipelineId ? `?pipelineId=${encodeURIComponent(pipelineId)}` : '';
  const r = await crmFetch<{ data: QuickTaskPreset[] }>(`/api/crm/quick-tasks${q}`);
  return r.data ?? [];
}

export interface CalendarTask {
  id: string;
  title: string;
  dueDate: string;
  startsAt: string | null;
  done: boolean;
  assigneeUserId: string | null;
  templateKey?: string | null;
  color?: string | null;
}

export interface NewTask {
  title: string;
  /** ГГГГ-ММ-ДД, местный день. */
  dueDate: string;
  /** ISO-момент; без него задача «на весь день». */
  startsAt?: string | null;
  dealId: string;
  templateKey?: string;
  color?: string | null;
}

/** POST /api/crm/calendar/tasks — быстрая задача в календаре CRM (routes/calendar.ts ~243). Исполнитель — я. */
export function createTask(t: NewTask) {
  return crmFetch<CalendarTask>('/api/crm/calendar/tasks', {
    method: 'POST',
    body: JSON.stringify({
      title: t.title,
      dueDate: t.dueDate,
      ...(t.startsAt ? { startsAt: t.startsAt } : {}),
      entityType: 'deal',
      entityId: t.dealId,
      ...(t.templateKey ? { templateKey: t.templateKey } : {}),
      ...(t.color ? { color: t.color } : {}),
    }),
  });
}

/** GET /api/crm/calendar/tasks?entityType=deal&entityId= → { data } — открытые и закрытые задачи сделки (routes/calendar.ts ~200). */
export async function getDealTasks(dealId: string): Promise<CalendarTask[]> {
  const r = await crmFetch<{ data: CalendarTask[] }>(
    `/api/crm/calendar/tasks?entityType=deal&entityId=${encodeURIComponent(dealId)}`,
  );
  return r.data ?? [];
}

// ── Лента касаний ──

export interface FeedItem {
  id: string;
  /** chat | touch | call | note | task | stage | doc | other */
  group: string;
  kind: string;
  source: string;
  title: string;
  body?: string | null;
  occurredAt: string;
  meta?: Record<string, unknown>;
}

/** GET /api/crm/timeline?entityType=deal&entityId= → { data } — та же единая лента, что в вебе (routes/timeline.ts, UnifiedFeed.tsx). */
export async function getFeed(dealId: string): Promise<FeedItem[]> {
  const r = await crmFetch<{ data: FeedItem[] }>(`/api/crm/timeline?entityType=deal&entityId=${encodeURIComponent(dealId)}`);
  return r.data ?? [];
}

// ── Звонок ──

export interface TelephonySettings {
  configured: boolean;
  /** sipuz — облачная АТС; own — своя АТС (софтфон в браузере). */
  provider: 'sipuz' | 'own';
  enabled: boolean;
  /** Логин сотрудника в АТС, закреплённый за человеком; пусто — АТС не знает, чей телефон вызывать. */
  myPbxLogin: string | null;
}

/** GET /api/crm/telephony/settings (routes/telephony.ts ~117). */
export const getTelephonySettings = () => crmFetch<TelephonySettings>('/api/crm/telephony/settings');

/** POST /api/crm/telephony/calls — «позвонить по клику» (routes/telephony.ts ~308). 409 CALL_DECISIONS_PENDING — сначала решить по прошлым звонкам. */
export const requestCall = (b: { phone: string; dealId?: string; personId?: string }) =>
  crmFetch<{ jobId: string; status: string }>('/api/crm/telephony/calls', { method: 'POST', body: JSON.stringify(b) });

export interface TelephonyJob { id: string; kind: string; status: 'pending' | 'running' | 'done' | 'failed'; reason: string | null }

/** GET /api/crm/telephony/jobs/:id (routes/telephony.ts ~492). */
export const getJob = (id: string) => crmFetch<TelephonyJob>(`/api/crm/telephony/jobs/${id}`);

export interface CallBrief { id: string; status: string; direction: string; startedAt: string | null; occurredAt: string | null; durationSeconds: number | null }

/** GET /api/crm/calls?dealId=&limit= → { items } — последние звонки сделки (routes/calls.ts ~71). */
export async function getDealCalls(dealId: string, limit = 3): Promise<CallBrief[]> {
  const r = await crmFetch<{ items: CallBrief[] }>(`/api/crm/calls?dealId=${encodeURIComponent(dealId)}&limit=${limit}`);
  return r.items ?? [];
}

export interface CallGate { pending: number; old: number; blocked: boolean; limit: number | null; minutes: number }

/** GET /api/crm/call-decisions/gate — «сначала решите по прошлым звонкам» (routes/callDecisions.ts ~35). */
export const getCallGate = () => crmFetch<CallGate>('/api/crm/call-decisions/gate');
